import { api } from '@/api/client'

export type SsoConnStatus = 'draft' | 'testing' | 'active' | 'paused' | 'error' | 'deleting'
export type SsoAppStatus = 'draft' | 'active' | 'paused' | 'error' | 'deleting'

export interface SsoConnection {
  id: number
  tenant_id: number
  name: string
  source_type: string
  keycloak_realm: string | null
  keycloak_ref: string | null
  status: SsoConnStatus
  // O backend nunca manda secret_ref da conexão (_sso_public_connection): só has_secret.
  has_secret?: boolean
  // Só a conexão que o backend elege como provedor da plataforma vem true.
  is_platform_provider?: boolean
  config: { entra_tenant_id?: string; client_id?: string; last_error?: string }
  // Só o provedor da plataforma; null quando ainda não houve ciclo de sincronização.
  diretorio_sync?: DiretorioSync | null
  created_at: string
  updated_at?: string
}

export interface DiretorioSync {
  ultima_sync_em: string | null
  // Código estável gravado pelo ciclo (ex.: graph_sem_permissao); null = último ciclo ok.
  ultimo_erro: string | null
  usuarios_ativos: number | null
}

// Seleção de atributos do diretório entregues a uma aplicação (config.diretorio).
export interface DiretorioSelecao {
  atributos: string[]
  finalidades: Record<string, string>
}

export interface AtributoCatalogo { chave: string; pessoal: boolean }

export interface SsoApplication {
  id: number
  // Cliente atendido: tenant da aplicação e o nome dele (a tela de plataforma lista todos).
  tenant_id: number
  tenant_name: string | null
  name: string
  protocol: 'oidc' | 'saml'
  keycloak_realm: string | null
  keycloak_client_id: string | null
  status: SsoAppStatus
  secret_ref: string | null
  config: {
    redirect_uris?: string[]; post_logout_redirect_uris?: string[]; last_error?: string
    default_role?: string
    diretorio?: DiretorioSelecao
  }
  created_at: string
  updated_at: string
}

// Resposta de POST /applications e POST /applications/{id}/provision: _sso_app_response
// em api.py, campo a campo (não é a linha SsoApplication).
export interface SsoAppProvisionResult {
  id: number
  tenant_id: number
  name: string | null
  protocol: 'oidc' | 'saml' | null
  status: SsoAppStatus
  keycloak_realm: string | null
  client_id: string | null
  issuer: string | null
  secret_arn: string | null
  claims: unknown
  last_error?: string | null
}

export interface EntraConnectionInput {
  keycloak_realm: string
  entra_tenant_id: string
  client_id: string
  client_secret: string
  // Nome exibido no login (displayName do realm e do IdP); omitido = "Entra <realm>".
  name?: string
}
export interface EntraConnectionUpdate {
  entra_tenant_id?: string
  client_id?: string
  client_secret?: string
  // Renomeia o que a pessoa vê; o realm e o alias técnicos não mudam.
  name?: string
}
export interface AppInput {
  name: string
  // Cliente atendido (tenant). O realm é sempre o do provedor da plataforma (backend).
  tenant_id: number
  client_id: string
  redirect_uris: string[]
  post_logout_redirect_uris: string[]
  // Role IAM que lê o client secret no Secrets Manager; obrigatória no backend.
  secret_consumer_principal: string
  // Papel concedido a todo membro do tenant Entra da conexão; vazio = sem papel padrão.
  default_role?: string
  // Atributos do diretório; omitido = o backend grava o padrão recomendado.
  diretorio?: DiretorioSelecao
}

// SSO é de plataforma (TASK-24956): um provedor só (o Entra da TecToy) e as aplicações de
// todos os clientes. Nada aqui depende do tenant selecionado; o gate é admin:platform.
const base = '/v1/sso'

export const listConnections = (): Promise<SsoConnection[]> =>
  api.get<SsoConnection[]>(`${base}/connections`).then((r) => r.data)

export const createEntraConnection = (body: EntraConnectionInput): Promise<SsoConnection> =>
  api.post<SsoConnection>(`${base}/connections`, {
    name: body.name?.trim() || `Entra ${body.keycloak_realm}`,
    source_type: 'entra_id',
    keycloak_realm: body.keycloak_realm,
    config: { entra_tenant_id: body.entra_tenant_id, client_id: body.client_id },
    client_secret: body.client_secret,
  }).then((r) => r.data)

export const updateEntraConnection = (id: number, body: EntraConnectionUpdate): Promise<SsoConnection> =>
  api.put<SsoConnection>(`${base}/connections/${id}`, body).then((r) => r.data)

export const setConnectionStatus = (
  id: number, status: 'testing' | 'paused' | 'active', clientSecret?: string,
): Promise<SsoConnection> =>
  api.put<SsoConnection>(
    `${base}/connections/${id}/status`,
    clientSecret ? { status, client_secret: clientSecret } : { status },
  ).then((r) => r.data)

export const deleteConnection = (id: number): Promise<void> =>
  api.delete(`${base}/connections/${id}`).then(() => undefined)

export const listApplications = (): Promise<SsoApplication[]> =>
  api.get<SsoApplication[]>(`${base}/applications`).then((r) => r.data)

export const createApplication = (body: AppInput): Promise<SsoAppProvisionResult> =>
  api.post<SsoAppProvisionResult>(`${base}/applications`, {
    name: body.name,
    protocol: 'oidc',
    tenant_id: body.tenant_id,
    config: {
      client_id: body.client_id,
      redirect_uris: body.redirect_uris,
      post_logout_redirect_uris: body.post_logout_redirect_uris,
      secret_consumer_principal: body.secret_consumer_principal,
      ...(body.default_role ? { default_role: body.default_role } : {}),
    },
    ...(body.diretorio ? { diretorio: body.diretorio } : {}),
  }).then((r) => r.data)

export const provisionApplication = (id: number): Promise<SsoAppProvisionResult> =>
  api.post<SsoAppProvisionResult>(`${base}/applications/${id}/provision`, {}).then((r) => r.data)

export const updateApplication = (
  id: number,
  // `default_role: null` remove o papel padrão; omitido não mexe. `tenant_id` move a aplicação
  // para outro cliente atendido (só vai quando mudou).
  body: Partial<Pick<AppInput, 'redirect_uris' | 'post_logout_redirect_uris'>>
    & { default_role?: string | null; tenant_id?: number; diretorio?: DiretorioSelecao },
): Promise<SsoApplication> =>
  api.put<SsoApplication>(`${base}/applications/${id}`, body).then((r) => r.data)

export const setApplicationStatus = (id: number, status: 'paused' | 'active'): Promise<SsoApplication> =>
  api.put<SsoApplication>(`${base}/applications/${id}/status`, { status }).then((r) => r.data)

export const getCatalogoDiretorio = (): Promise<AtributoCatalogo[]> =>
  api.get<AtributoCatalogo[]>(`${base}/diretorio/catalogo`).then((r) => r.data)

export interface ClienteTenant { id: number; name: string; environment?: string | null; is_active?: number | boolean }

// Cliente atendido: a lista de todos os tenants, pelo mesmo endpoint das telas de admin
// de plataforma (gate admin:platform, o mesmo desta tela).
export const listClientes = (): Promise<ClienteTenant[]> =>
  api.get<{ items: ClienteTenant[] }>('/v1/admin/tenants').then((r) => r.data.items ?? [])

// Nenhum endpoint /api/v1 expõe SSO_PUBLIC_BASE_URL (conferido em api.py); o valor
// espelha o default de consoleai/config.py::SSO_PUBLIC_BASE_URL. Se o backend passar a
// expô-lo, troque esta constante pela resposta.
export const SSO_PUBLIC_BASE_URL = 'https://sso.console.amazonasinovare.com.br'

export const entraRedirectUri = (realm: string): string =>
  `${SSO_PUBLIC_BASE_URL}/realms/${realm}/broker/entra-${realm}/endpoint`

export const entraIssuer = (tenantGuid: string): string =>
  `https://login.microsoftonline.com/${tenantGuid}/v2.0`
